import { type GetResidentResourcesParams, residentDashboardApis } from '../api';
import type { UsePaymentActivitiesParams } from '../types/dashboard';
import type { FinancialFiltersType } from '@/features/financials/components/FinancialHistoryFilters';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

export const useResidentDashboardOverview = () => {
  return useQuery({
    queryKey: ['resident', 'dashboard', 'overview'],
    queryFn: residentDashboardApis.getOverview,
    staleTime: 2 * 60 * 1000,
  });
};

export const useResidentDashboardActivities = (
  params: UsePaymentActivitiesParams,
) => {
  return useQuery({
    queryKey: ['resident-dashboard-activities', params],
    queryFn: () =>
      residentDashboardApis.getActivities({
        page: params.page,
        limit: params.limit,
        status: params.status,
        type: params.type,
      }),
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        if (![408, 429].includes(error.response.status)) return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useResidentAnnouncementOverview = () => {
  return useQuery({
    queryKey: ['residentAnnouncementOverview'],
    queryFn: residentDashboardApis.getResidentAnnouncementOverview,
  });
};

export const useResidentDashboardPaymentHistory = (
  params: FinancialFiltersType,
) => {
  return useQuery({
    queryKey: ['residentDashboardPaymentsHistory', params],
    queryFn: () => residentDashboardApis.getPaymentHistory(params),
    staleTime: 2 * 60 * 1000,
  });
};

export const useResidentResources = (params: GetResidentResourcesParams) => {
  return useQuery({
    queryKey: ['resident-resources', params],
    queryFn: () => residentDashboardApis.getMyResources(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useSignResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, path }: { id: string; path: string }) =>
      residentDashboardApis.signResource(id, path),
    onSuccess: () => {
      toast.success('Document signed successfully');
      queryClient.invalidateQueries({ queryKey: ['resident-resources'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to sign document');
    },
  });
};

export const useResidentResourceDetails = (id: string) => {
  return useQuery({
    queryKey: ['resident-resource-details', id],
    queryFn: () => residentDashboardApis.getResidentResourceById(id),
    enabled: !!id,
  });
};
